/**
 Copyright Highway9 Networks Inc.
 */
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "..";
import { UserInfo, Permission } from "~/types/user";
import { permissionsMapFromApi } from "~/helpers/rbacUtils";
import { PermissionsMap } from "~/types/rbac";

export interface AuthState {
  user: UserInfo | null;
  token: any;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: any;
  currentRoute: string | null;
  permissions: PermissionsMap;
  permissionsLoading: boolean;
  activeRoleId: string | null;
  originalPermissions: PermissionsMap | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  currentRoute: null,
  permissions: {},
  permissionsLoading: false,
  activeRoleId: null,
  originalPermissions: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login: (state, action) => {
      state.isLoading = true;
    },
    setUser: (state, action : PayloadAction<UserInfo>) => {
      state.user = action.payload;
      if (action.payload) {
        state.isAuthenticated = true;
        state.isLoading = false;
      }
    },
    setCurrentRoute: (state, action: PayloadAction<string>) => {
      state.currentRoute = action.payload;
    },
    setUserPermissions(state, action: PayloadAction<Permission[]>) {
      state.permissions = permissionsMapFromApi(action.payload);
      // When real permissions are refreshed, clear any active role override
      state.activeRoleId = null;
      state.originalPermissions = null;
    },
    setUserPermissionsLoading(state, action: PayloadAction<boolean>) {
      state.permissionsLoading = action.payload;
    },
    applyRoleOverride(state, action: PayloadAction<{ roleId: string; permissions: Permission[] }>) {
      if (!state.originalPermissions) {
        state.originalPermissions = state.permissions;
      }
      state.activeRoleId = action.payload.roleId;
      state.permissions = permissionsMapFromApi(action.payload.permissions);
    },
    clearRoleOverride(state) {
      if (state.originalPermissions) {
        state.permissions = state.originalPermissions;
        state.originalPermissions = null;
      }
      state.activeRoleId = null;
    },
  },
});

export const { login, setUser, setCurrentRoute, setUserPermissions, setUserPermissionsLoading, applyRoleOverride, clearRoleOverride } = authSlice.actions;

export default authSlice.reducer;

export const authUser = (state: RootState) => state.auth.user;
export const authToken = (state: RootState) => state.auth.token;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsLoading = (state: RootState) => state.auth.isLoading;
export const selectUserPermssions = (state: RootState) => state.auth.permissions;
export const selectUserPermssionsLoading = (state: RootState) => state.auth.permissionsLoading;
export const selectActiveRoleId = (state: RootState) => state.auth.activeRoleId;
